import React, { useEffect } from 'react';
import { CheckCircle, FileText, ExternalLink } from 'lucide-react';

export const Certifications = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pdfCertificates = [
    {
      id: 1,
      name: "BRC Global Standard",
      description: "Food Safety - Gıda Güvenliği",
      url: "https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/duk2i5h1_IMG_20260406_0002.pdf"
    },
    {
      id: 2,
      name: "ISO 9001:2015",
      description: "Kalite Yönetim Sistemi",
      url: "https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/em1lnor2_IMG_20260406_0009.pdf"
    },
    {
      id: 3,
      name: "EN ISO 15593",
      description: "Ambalaj Standardı",
      url: "https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/92kun45b_IMG_20260406_0005.pdf"
    },
    {
      id: 4,
      name: "ISO 45001:2018",
      description: "İş Sağlığı ve Güvenliği",
      url: "https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/2xluwds5_IMG_20260406_0003.pdf"
    },
    {
      id: 5,
      name: "ISO 22301:2019",
      description: "İş Sürekliliği Yönetimi",
      url: "https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/rntr0fk6_IMG_20260406_0004.pdf"
    },
    {
      id: 6,
      name: "ISO 22000:2018",
      description: "Gıda Güvenliği Yönetim Sistemi",
      url: "https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/i2su0eaz_IMG_20260406_0006.pdf"
    },
    {
      id: 7,
      name: "ISO 14001:2015",
      description: "Çevre Yönetim Sistemi",
      url: "https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/tpydk4vi_IMG_20260406_0007.pdf"
    },
    {
      id: 8,
      name: "ISO 10002:2018",
      description: "Müşteri Memnuniyeti Yönetimi",
      url: "https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/bybbpa7c_IMG_20260406_0008.pdf"
    },
    {
      id: 9,
      name: "Doğaya Saygı Belgesi",
      description: "Çevre ve Sürdürülebilirlik",
      url: "https://customer-assets.emergentagent.com/job_packaging-hub-14/artifacts/quhv9iw9_IMG_20260406_0001.pdf"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-lime-600 to-lime-700">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-white mb-6">
              Sertifikalarımız
            </h1>
            <p className="text-xl text-lime-100">
              Uluslararası standartlarda kalite ve güvenilirlik belgelerimiz
            </p>
          </div>
        </div>
      </section>

      {/* PDF Certificates Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 max-w-7xl mx-auto">
            {pdfCertificates.map((cert) => (
              <a
                key={cert.id}
                href={cert.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white p-3 rounded-lg border border-lime-100 hover:border-lime-400 hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer"
              >
                <div className="flex flex-col items-center text-center">
                  {/* PDF Preview Thumbnail */}
                  <div className="w-full h-32 bg-gray-100 rounded-lg mb-2 overflow-hidden relative border border-gray-200 group-hover:border-lime-300 transition-colors">
                    <iframe 
                      src={`${cert.url}#toolbar=0&navpanes=0&scrollbar=0`}
                      className="w-full h-full pointer-events-none scale-150 origin-top-left"
                      style={{ transform: 'scale(1.5)' }}
                      title={cert.name}
                    />
                    <div className="absolute inset-0 bg-transparent pointer-events-none"></div>
                    {/* Overlay icon on hover */}
                    <div className="absolute inset-0 bg-lime-600/0 group-hover:bg-lime-600/10 transition-all flex items-center justify-center">
                      <ExternalLink className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  
                  <h3 className="text-xs font-bold text-gray-900 mb-1 leading-tight">
                    {cert.name}
                  </h3>
                  <p className="text-[10px] text-gray-600 mb-2 leading-tight">
                    {cert.description}
                  </p>
                  <div className="flex items-center space-x-1 text-lime-600 group-hover:text-lime-700">
                    <span className="text-[10px] font-medium">Görüntüle</span>
                    <ExternalLink className="w-2.5 h-2.5" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability Section */}
      <section className="py-20 bg-gradient-to-br from-lime-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1669384537216-24740a56a2d5"
                    alt="Sustainability"
                    className="w-full h-96 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-lime-900/80 to-transparent"></div>
                </div>
              </div>
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Sürdürülebilirlik Taahhüdümüz
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Çevre dostu üretim anlayışımız ve FSC sertifikalı malzemeler kullanımımızla, 
                  gelecek nesillere yaşanabilir bir dünya bırakmayı hedefliyoruz.
                </p>
                <div className="space-y-4">
                  {[
                    'Geri dönüştürülebilir malzemeler',
                    'Enerji verimli üretim',
                    'Sıfır atık hedefi',
                    'Sürdürülebilir orman kaynaklı hammadde'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-lime-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-lime-600" />
                      </div>
                      <span className="text-gray-700 text-lg">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Standards Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-12">
              Uyduğumuz Standartlar
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Kalite Standartları',
                  items: ['ISO 9001:2015', 'Müşteri Memnuniyeti', 'Sürekli İyileştirme']
                },
                {
                  title: 'Çevre Standartları',
                  items: ['ISO 14001:2015', 'FSC Sertifikası', 'Karbon Ayak İzi']
                },
                {
                  title: 'Gıda Güvenliği',
                  items: ['ISO 22000', 'HACCP', 'Hijyen Standartları']
                }
              ].map((standard, idx) => (
                <div key={idx} className="bg-gradient-to-br from-lime-50 to-white p-8 rounded-2xl border border-lime-100 hover:shadow-xl transition-all">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{standard.title}</h3>
                  <ul className="space-y-3">
                    {standard.items.map((item, i) => (
                      <li key={i} className="flex items-center justify-center space-x-2 text-gray-700">
                        <CheckCircle className="w-4 h-4 text-lime-600 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
